import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { NewJobOrder } from './pages/NewJobOrder';
import { JobOrders } from './pages/JobOrders';
import { JobOrderDetail } from './pages/JobOrderDetail';
import { ApprovedJobs } from './pages/ApprovedJobs';
import { Procurement } from './pages/Procurement';
import ProcurementReports from './pages/ProcurementReports';
import { Production } from './pages/Production';
import { ProductionReports } from './pages/ProductionReports';
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import { Reports } from './pages/Reports';
import { CustomersList } from './pages/CustomersList';
import { CustomerDetail } from './pages/CustomerDetail';
import { EditJobOrder } from './pages/EditJobOrder';
import { DeletedJobOrders } from './pages/DeletedJobOrders';
import { InstallationDashboard } from './pages/InstallationDashboard';
import { InstallationDispatch } from './pages/InstallationDispatch';
import { InstallationReports } from './pages/InstallationReports';
import { SalesDashboard } from './pages/SalesDashboard';
import ForcePasswordChange from './pages/ForcePasswordChange';
import { InventoryDashboard } from './pages/inventory/InventoryDashboard';
import { InventoryMasters } from './pages/inventory/InventoryMasters';
import { InventoryJOList } from './pages/inventory/InventoryJOList';
import { InventoryJODetail } from './pages/inventory/InventoryJODetail';
import { StockOperations } from './pages/inventory/StockOperations';
import { ProcurementRequirement } from './pages/inventory/ProcurementRequirement';
import { InventoryReports } from './pages/inventory/InventoryReports';
import { MobileQuotations } from './pages/admin/MobileQuotations';
import { MobileQuotationDetail } from './pages/admin/MobileQuotationDetail';
import { MobileJobOrders } from './pages/admin/MobileJobOrders';
import { MobileJobOrderDetail } from './pages/admin/MobileJobOrderDetail';
import { OutstandingReport } from './pages/admin/OutstandingReport';
import { NotificationsPage } from './pages/admin/NotificationsPage';
import { NotificationSettings } from './pages/admin/NotificationSettings';
import { IntegrationsStatus } from './pages/admin/IntegrationsStatus';
import CompanySettings from './pages/admin/CompanySettings';
import CustomerMaster from './pages/admin/CustomerMaster';
import PaymentReceipts from './pages/admin/PaymentReceipts';
import ProcurementDashboard from './pages/admin/ProcurementDashboard';
import ProductionPlanning from './pages/admin/ProductionPlanning';
import DeliveryCalendar from './pages/admin/DeliveryCalendar';
import { Toaster } from './components/ui/sonner';
import './index.css';

// Stable role arrays
const ROLES_ADMIN = ['admin'];
const ROLES_INVENTORY = ['admin', 'inventory'];
const ROLES_EXEC_ADMIN = ['executive', 'admin'];
const ROLES_ALL = ['executive', 'procurement', 'admin', 'production', 'installation', 'sales', 'inventory'];
const ROLES_APPROVED = ['admin', 'procurement', 'production'];
const ROLES_PROCUREMENT = ['procurement', 'admin'];
const ROLES_PRODUCTION = ['production', 'admin'];
const ROLES_INSTALLATION = ['installation', 'admin'];
const ROLES_SALES = ['sales'];
const ROLES_ADMIN_SALES = ['admin', 'sales'];

const ROLE_DEFAULT_ROUTES = {
  admin: '/dashboard',
  executive: '/job-orders',
  procurement: '/procurement',
  production: '/production',
  installation: '/installation',
  sales: '/sales',
  inventory: '/inventory'
};

function ForceChangePasswordGuard({ user, getDefaultRoute }) {
  if (!user) return <Navigate to="/login" />;
  if (user.force_password_change) return <ForcePasswordChange />;
  return <Navigate to={getDefaultRoute()} />;
}

// Route configuration - grouped by role
const ROUTE_CONFIG = [
  // Admin routes
  { path: '/dashboard', component: Dashboard, roles: ROLES_ADMIN },
  { path: '/users', component: Users, roles: ROLES_ADMIN },
  { path: '/customers', component: CustomersList, roles: ROLES_ADMIN },
  { path: '/customers/:mobile', component: CustomerDetail, roles: ROLES_ADMIN_SALES },
  { path: '/settings', component: Settings, roles: ROLES_ADMIN },
  { path: '/reports', component: Reports, roles: ROLES_ADMIN },
  { path: '/deleted-jobs', component: DeletedJobOrders, roles: ROLES_ADMIN },
  // Executive + Admin routes
  { path: '/job-orders/new', component: NewJobOrder, roles: ROLES_EXEC_ADMIN },
  { path: '/job-orders', component: JobOrders, roles: ROLES_EXEC_ADMIN },
  { path: '/job-orders/:jo_number/edit', component: EditJobOrder, roles: ROLES_EXEC_ADMIN },
  // Shared routes
  { path: '/job-orders/:jo_number', component: JobOrderDetail, roles: ROLES_ALL },
  { path: '/approved-jobs', component: ApprovedJobs, roles: ROLES_APPROVED },
  // Department routes
  { path: '/procurement', component: Procurement, roles: ROLES_PROCUREMENT },
  { path: '/procurement/reports', component: ProcurementReports, roles: ROLES_PROCUREMENT },
  { path: '/production', component: Production, roles: ROLES_PRODUCTION },
  { path: '/production/reports', component: ProductionReports, roles: ROLES_PRODUCTION },
  { path: '/installation', component: InstallationDashboard, roles: ROLES_INSTALLATION },
  { path: '/installation/dispatch', component: InstallationDispatch, roles: ROLES_INSTALLATION },
  { path: '/installation/reports', component: InstallationReports, roles: ROLES_INSTALLATION },
  { path: '/sales', component: SalesDashboard, roles: ROLES_SALES },
  // Inventory routes (admin + inventory role)
  { path: '/inventory', component: InventoryDashboard, roles: ROLES_INVENTORY },
  { path: '/inventory/masters', component: InventoryMasters, roles: ROLES_INVENTORY },
  { path: '/inventory/job-orders', component: InventoryJOList, roles: ROLES_INVENTORY },
  { path: '/inventory/jo/:jo_number', component: InventoryJODetail, roles: ROLES_INVENTORY },
  { path: '/inventory/stock', component: StockOperations, roles: ROLES_INVENTORY },
  { path: '/inventory/procurement', component: ProcurementRequirement, roles: ROLES_INVENTORY },
  { path: '/inventory/reports', component: InventoryReports, roles: ROLES_INVENTORY },
  // Admin — Mobile App Mirror
  { path: '/admin/mobile-quotations', component: MobileQuotations, roles: ROLES_ADMIN_SALES },
  { path: '/admin/mobile-quotations/:quotation_number', component: MobileQuotationDetail, roles: ROLES_ADMIN_SALES },
  { path: '/admin/mobile-job-orders', component: MobileJobOrders, roles: ROLES_ADMIN_SALES },
  { path: '/admin/mobile-job-orders/:jo_number', component: MobileJobOrderDetail, roles: ROLES_ADMIN_SALES },
  { path: '/admin/outstanding-report', component: OutstandingReport, roles: ROLES_ADMIN },
  { path: '/admin/notifications', component: NotificationsPage, roles: ROLES_ADMIN },
  { path: '/admin/notification-settings', component: NotificationSettings, roles: ROLES_ADMIN },
  { path: '/admin/integrations', component: IntegrationsStatus, roles: ROLES_ADMIN },
  { path: '/admin/company-settings', component: CompanySettings, roles: ROLES_ADMIN },
  { path: '/admin/customers', component: CustomerMaster, roles: ROLES_ADMIN_SALES },
  { path: '/admin/receipts', component: PaymentReceipts, roles: ROLES_ADMIN },
  { path: '/admin/production-planning', component: ProductionPlanning, roles: ROLES_ADMIN },
  { path: '/admin/delivery-calendar', component: DeliveryCalendar, roles: ROLES_ADMIN },
  { path: '/admin/procurement', component: ProcurementDashboard, roles: ROLES_PROCUREMENT },
];

function AppRoutes() {
  const { user } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return '/login';
    return ROLE_DEFAULT_ROUTES[user.role] || '/';
  };

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} /> : <Login />} />
      
      <Route path="/force-change-password" element={
        <ForceChangePasswordGuard user={user} getDefaultRoute={getDefaultRoute} />
      } />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Navigate to={getDefaultRoute()} />
        </ProtectedRoute>
      } />

      {ROUTE_CONFIG.map(({ path, component: Component, roles }) => (
        <Route key={path} path={path} element={
          <ProtectedRoute allowedRoles={roles}>
            <Component />
          </ProtectedRoute>
        } />
      ))}
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
